import React, { useState, useEffect, useCallback, useMemo } from 'react';
import _ from 'lodash';
import { formatDate } from './dateUtils';

const FormField = ({
                       fieldKey,
                       value,
                       onUpdate,
                       fieldType = 'text',
                       options = null,
                       isEditable = false,
                       placeholder = 'Enter value',
                       fieldFormat = null,
                       metadata = {}
                   }) => {
    const [currentValue, setCurrentValue] = useState(value);
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
        setCurrentValue(value);
    }, [value]);

    const debouncedUpdate = useMemo(
        () => _.debounce((newValue, key, updateFn) => {
            try {
                setIsUpdating(true);
                updateFn(key, newValue);
            } catch (error) {
                console.error('Error updating field:', error);
                setCurrentValue(value);
            } finally {
                setIsUpdating(false);
            }
        }, 500),
        [value]
    );

    const handleTextChange = useCallback(
        (newValue) => {
            if (!onUpdate || newValue === value) return;
            debouncedUpdate(newValue, fieldKey, onUpdate);
        },
        [debouncedUpdate, fieldKey, onUpdate, value]
    );

    useEffect(() => {
        return () => {
            debouncedUpdate.cancel();
        };
    }, [debouncedUpdate]);

    const processValueForDisplay = (val) => {
        if (val === null || val === undefined) return '-';

        switch (fieldType) {
            case 'identity':
                return val.displayName || val.uniqueName || '-';
            case 'html':
                return <div dangerouslySetInnerHTML={{ __html: val }} />;
            case 'treePath':
                return val.split('\\').pop() || val;
            case 'boolean':
                return val ? 'Yes' : 'No';
            case 'datetime':
                return formatDate(val);
            case 'select':
                const option = options?.find(opt =>
                    opt.value.toString() === val.toString()
                );
                return option ? option.label : val;
            default:
                return val.toString();
        }
    };

    const handleImmediateChange = async (newValue) => {
        setCurrentValue(newValue);
        if (!onUpdate) return;

        let processedValue;
        switch (fieldType) {
            case 'number':
                processedValue = Number(newValue);
                break;
            case 'boolean':
                processedValue = Boolean(newValue);
                break;
            case 'datetime':
                if (newValue) {
                    // Keep original time portion when updating date
                    const originalTime = value?.split('T')[1] || '00:00:00.000Z';
                    processedValue = `${newValue}T${originalTime}`;
                }
                break;
            case 'select':
                processedValue = options?.find(opt =>
                    opt.value.toString() === newValue.toString()
                )?.value ?? newValue;
                break;
            default:
                processedValue = newValue;
        }

        try {
            setIsUpdating(true);
            await onUpdate(fieldKey, processedValue);
        } catch (error) {
            console.error('Error updating field:', error);
            setCurrentValue(value);
        } finally {
            setIsUpdating(false);
        }
    };

    if (!isEditable) {
        return (
            <div className="px-3 py-2 bg-gray-50 border rounded-md text-gray-700">
                {processValueForDisplay(value)}
            </div>
        );
    }

    switch (fieldType) {
        case 'datetime':
            const formattedValue = value ? value.split('T')[0] : ''; // Get YYYY-MM-DD for input
            return (
                <input
                    type="date"
                    value={formattedValue}
                    onChange={(e) => handleImmediateChange(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    disabled={isUpdating}
                />
            );

        case 'select':
            return (
                <select
                    value={currentValue?.toString() || ''}
                    onChange={(e) => handleImmediateChange(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    disabled={isUpdating}
                >
                    {options?.map(option => (
                        <option key={option.value} value={option.value.toString()}>
                            {option.label}
                        </option>
                    ))}
                </select>
            );

        case 'boolean':
            return (
                <select
                    value={currentValue?.toString() || 'false'}
                    onChange={(e) => handleImmediateChange(e.target.value === 'true')}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    disabled={isUpdating}
                >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                </select>
            );

        case 'html':
            return (
                <textarea
                    value={currentValue || ''}
                    onChange={(e) => {
                        setCurrentValue(e.target.value);
                        handleTextChange(e.target.value);
                    }}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    rows={4}
                    disabled={isUpdating}
                />
            );

        default:
            return (
                <input
                    type="text"
                    value={currentValue || ''}
                    onChange={(e) => {
                        setCurrentValue(e.target.value);
                        handleTextChange(e.target.value);
                    }}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    placeholder={placeholder}
                    disabled={isUpdating}
                />
            );
    }
};

export default FormField;